/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
} from 'primereact/autocomplete';
import {
  faArrowDownLong,
  faArrowUpLong,
} from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SingleHorizontalBarChart } from '@/components/Charts';
import { DropdownField, AutoCompleteField, LoadingSpinner } from '@/components';
import { DualTextCard } from '@/components/Cards';
import { getCurrentDateTime } from '@/utils/getCurrentDateTime';
import { useRoRoPage } from '@/hooks/Dashboard/useRoRoPage';
import { formatDateAndTime } from '@/utils/formatDate';
import dualTextCardIcon from '../../../../../assets/icons/dashboard/tempo-icon.svg';

import styles from './RoRoPage.module.scss';
import DetallesComponent from './DetallesTable/DetallesComponent';

interface Viaje {
  name: string;
  value: string;
  llegada: string;
  salida: string;
}

export const RoRoPage = () => {
  const { t } = useTranslation('dashboardPage');
  const lastUpdate = getCurrentDateTime();
  const {
    armadoresListForDropdown,
    currentArmadorRoRo,
    viajesListForDropdownRoRo,
    currentViajeRoRo,
    setCurrentViajeRoRo,
    setCurrentArmadorRoRo,
    roRoPageData,
    setRoRoPageData,
    loadingArmadores,
    loadingViajes,
    loadingPageData,
    // pageData,
  } = useRoRoPage();
  const [viajes, setViajes] = useState<Viaje[]>([]);
  const [selectedViaje, setSelectedViaje] = useState<Viaje | null>(null);
  const [filteredViajes, setFilteredViajes] = useState<Viaje[]>();

  const handleArmadorChange = (e: {
    value: string | null;
    target?: { name: string };
  }) => {
    setRoRoPageData(null);

    setCurrentArmadorRoRo({
      value: e.value ?? '',
      name: e.value ?? '',
    });
  };

  useEffect(() => {
    setSelectedViaje(currentViajeRoRo);
  }, [currentViajeRoRo]);

  const handleTripChange = (event: AutoCompleteChangeEvent) => {
    const tripName = event.value;
    setSelectedViaje(tripName);

    const code = viajesListForDropdownRoRo?.find(
      (trip) => trip.value === tripName.value
    );

    if (code) {
      setCurrentViajeRoRo({
        value: tripName.value ?? '',
        name: tripName.name ?? '',
        llegada: tripName.llegada ?? '',
        salida: tripName.salida ?? '',
      });
    }
  };

  const search = (event: AutoCompleteCompleteEvent) => {
    let filteredViajes2;

    if (!event.query.trim().length) {
      filteredViajes2 = [...viajes];
    } else {
      filteredViajes2 = viajes.filter((viaje) => {
        return viaje.name.toLowerCase().startsWith(event.query.toLowerCase());
      });
    }

    setFilteredViajes(filteredViajes2);
  };

  useEffect(() => {
    if (viajesListForDropdownRoRo) {
      setViajes(viajesListForDropdownRoRo);
    }
  }, [viajesListForDropdownRoRo]);

  /* 
  useEffect(() => {
    if (viajesListForDropdownRoRo) {
      setSelectedViaje(viajesListForDropdownRoRo[0]);
    }
  }, [viajesListForDropdownRoRo]);
  */
  if (loadingArmadores || loadingPageData || loadingViajes)
    return (
      <div style={{ overflow: 'hidden' }} className={styles.dashboard}>
        <LoadingSpinner className={styles.spinner} />
      </div>
    );

  return (
    <>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div className={styles.filterAndUpdatedAtContainer}>
            <DropdownField
              id="armador"
              name="armador"
              options={armadoresListForDropdown}
              value={currentArmadorRoRo.name}
              label={t('shipPage.armador')}
              className={styles.selectDropdown}
              placeholder={t('shipPage.seleccionar_armador')}
              onChange={handleArmadorChange}
            />
            <p className={styles.lastUpdate}>
              {t('shipPage.ultima_actualizacion')} <span>{lastUpdate}</span>
            </p>
          </div>
        </div>
      </div>
      <div className={styles.dashboard}>
        <div className={styles.leftColumn}>
          <div className={styles.shipInfo}>
            <div className={styles.filterContainer}>
              <div className={styles.autoComplete}>
                <AutoCompleteField
                  placeholder={t('roRoPage.busqueda_viaje')}
                  label={t('roRoPage.busqueda_viaje')}
                  field="name"
                  value={selectedViaje}
                  suggestions={filteredViajes}
                  completeMethod={search}
                  dropdown
                  onChange={handleTripChange}
                  className={styles.autoComplete}
                  isLoading={
                    loadingArmadores || loadingPageData || loadingViajes
                  }
                  labelClassname={styles.labelAutocomplete}
                />
              </div>
            </div>
          </div>
          <div className="row flex">
            <div className="md:col-5 col-12">
              <DualTextCard
                title1={t('shipPage.comienzo_operaciones')}
                detail1={formatDateAndTime(currentViajeRoRo.llegada ?? '')}
                title2={t('shipPage.fin_operaciones')}
                detail2={formatDateAndTime(currentViajeRoRo.salida ?? '')}
                backgroundColor="#4780bf"
                dividerColor="#223466"
                textColor="#FFFFFF"
                icon={dualTextCardIcon}
                className={classNames(styles.dualTextCardContainer)}
              />
            </div>
          </div>
          <div className={styles.dataContainerCard}>
            <div className={styles.operations}>
              <div className={`${styles.operation} row`}>
                <div className="md:col-6 col-12">
                  <div className={styles.operationHeader}>
                    <div className={styles.letterAndIconContainer}>
                      <FontAwesomeIcon
                        className={styles.operationIcon}
                        color="#052e63"
                        icon={faArrowDownLong}
                      />
                      <p>{t('roRoPage.descarga')}</p>
                    </div>
                    <span>
                      {t('roRoPage.vehiculos')}{' '}
                      {roRoPageData?.desCargaEquiposRealizados ?? 0}/
                      {roRoPageData?.desCargaEquiposTotal ?? 0}{' '}
                      {t('roRoPage.descargados')}
                    </span>
                  </div>

                  <SingleHorizontalBarChart
                    total={roRoPageData?.desCargaEquiposTotal ?? 0}
                    metric={roRoPageData?.desCargaEquiposRealizados ?? 0}
                  />
                </div>
                <div className="md:col-6 col-12">
                  <div className={styles.operationHeader}>
                    <div className={styles.letterAndIconContainer}>
                      <FontAwesomeIcon
                        className={styles.operationIcon}
                        color="#052e63"
                        icon={faArrowUpLong}
                      />
                      <p>{t('roRoPage.carga')}</p>
                    </div>
                    <span>
                      {t('roRoPage.vehiculos')}{' '}
                      {roRoPageData?.cargaEquiposRealizados ?? 0}/
                      {roRoPageData?.cargaEquiposTotal ?? 0}{' '}
                      {t('roRoPage.cargados')}
                    </span>
                  </div>

                  <SingleHorizontalBarChart
                    total={roRoPageData?.cargaEquiposTotal ?? 0}
                    metric={roRoPageData?.cargaEquiposRealizados ?? 0}
                  />
                </div>
              </div>
            </div>

            <div>
              <DetallesComponent
                detalles={roRoPageData?.detalles}
                isLoading={loadingArmadores || loadingPageData || loadingViajes}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
